import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@components/ui';
import { useAppDispatch } from '../../../store';
import {
    fetchAIPurchaseBrands,
    useAIPurchaseBrands,
    useAIPurchaseBrandsPagination,
    useAIPurchaseLoading,
    useAIPurchaseError,
} from '../../../store/slices/aiPurchaseAnalyticsSlice';
import {
    AI_PURCHASE_BRAND_LIST_DEFAULTS,
    AI_PURCHASE_BRAND_SEARCH_DEBOUNCE_MS,
    AI_PURCHASE_BRAND_SORT_OPTIONS,
    AI_PURCHASE_BRAND_DEFAULT_SORT,
} from '../../../constants/api.constants';
import { formatCount } from './aiPurchaseAnalyticsUtils';
import PercentRow from './PercentRow';

const BRAND_SORT_OPTIONS = Object.values(AI_PURCHASE_BRAND_SORT_OPTIONS);

interface BrandBreakdownGridProps {
    state: string;
    fromDate: string;
    toDate: string;
    onSelectBrand: (brandId: string, brandName: string) => void;
}

/**
 * Per-brand AI purchase adoption breakdown within one state, as a searchable,
 * paginated table backed by real server-side search + pagination + sort on the
 * `by-state/:state/brands` endpoint (see ai-purchase-tracking-plan.md §5.2) — the
 * search input is debounced before triggering a new fetch, and Previous/Next use the
 * real `pagination` metadata returned by the API rather than a client-side array
 * length. Sorted by adoption % by purchases (server-side via `sortValue`) with
 * higher totalActivePurchases as the tie-break. Rows navigate to the store
 * breakdown page for that brand.
 */
export default function BrandBreakdownGrid({
    state,
    fromDate,
    toDate,
    onSelectBrand,
}: BrandBreakdownGridProps) {
    const dispatch = useAppDispatch();

    const brands = useAIPurchaseBrands();
    const pagination = useAIPurchaseBrandsPagination();
    const loading = useAIPurchaseLoading();
    const error = useAIPurchaseError();

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentSort, setCurrentSort] = useState<string>(AI_PURCHASE_BRAND_DEFAULT_SORT);
    const [showSortMenu, setShowSortMenu] = useState(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const sortMenuRef = useRef<HTMLDivElement>(null);

    const limit = AI_PURCHASE_BRAND_LIST_DEFAULTS.LIMIT;

    // Debounce the search input before it feeds into the fetch effect below, and
    // reset back to page 1 whenever the (debounced) search term actually changes.
    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, AI_PURCHASE_BRAND_SEARCH_DEBOUNCE_MS);
    };

    const handleSortChange = (sortValue: string) => {
        setCurrentSort(sortValue);
        setCurrentPage(1);
        setShowSortMenu(false);
    };

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Close sort menu on outside click.
    useEffect(() => {
        if (!showSortMenu) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setShowSortMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSortMenu]);

    // Reset to page 1 whenever the state or date range changes (a fresh scope).
    useEffect(() => {
        setCurrentPage(1);
    }, [state, fromDate, toDate]);

    useEffect(() => {
        if (!state) return;
        dispatch(
            fetchAIPurchaseBrands({
                state,
                fromDate,
                toDate,
                search: debouncedSearch.trim() || undefined,
                skip: (currentPage - 1) * limit,
                limit,
                sortValue: currentSort,
            })
        );
    }, [dispatch, state, fromDate, toDate, debouncedSearch, currentPage, limit, currentSort]);

    const totalPages = pagination?.totalPages ?? 1;
    const total = pagination?.total ?? 0;
    const skip = pagination?.skip ?? 0;
    const isLoading = loading.brands === 'loading';
    const errorMessage = error.brands;

    if (errorMessage) {
        return <p className="text-sm text-[#dc2626] text-center py-6">{errorMessage}</p>;
    }

    return (
        <div className="space-y-4">
            {/* Search + Sort */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative max-w-xs w-full">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search brands..."
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-[#e2e8f0] rounded-lg bg-white text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/20 focus:border-[#6e64f0] transition-colors"
                    />
                </div>

                <div className="relative" ref={sortMenuRef}>
                    <button
                        type="button"
                        onClick={() => setShowSortMenu((prev) => !prev)}
                        className="px-4 py-2 border border-[#e2e8f0] rounded-lg transition-all flex items-center gap-2 text-[#64748b] hover:bg-[#f8fafc] text-sm font-medium whitespace-nowrap"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                            />
                        </svg>
                        <span className="max-w-[220px] truncate">{currentSort}</span>
                        <svg
                            className="w-3.5 h-3.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    {showSortMenu && (
                        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-20">
                            <div className="p-2">
                                {BRAND_SORT_OPTIONS.map((opt) => (
                                    <button
                                        type="button"
                                        key={opt}
                                        onClick={() => handleSortChange(opt)}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between text-sm ${
                                            currentSort === opt
                                                ? 'bg-[#f1f5f9] text-[#6e64f0] font-medium'
                                                : 'text-[#64748b] hover:bg-[#f8fafc]'
                                        }`}
                                    >
                                        {opt}
                                        {currentSort === opt && (
                                            <svg
                                                className="w-4 h-4 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-2 border-[#6e64f0] border-t-transparent rounded-full animate-spin" />
                </div>
            ) : brands.length === 0 && !debouncedSearch.trim() ? (
                <p className="text-sm text-[#64748b] text-center py-6">
                    No brands with AI purchase data found in {state} for this date range.
                </p>
            ) : (
                <>
                    <Card>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                                Brand
                                            </th>
                                            <th className="px-6 py-4 text-right text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                                Purchases
                                            </th>
                                            <th className="px-6 py-4 text-right text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                                Line items
                                            </th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                                Adoption by purchases
                                            </th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                                Adoption by line items
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#e2e8f0]">
                                        {brands.length > 0 ? (
                                            brands.map((brand) => (
                                                <tr
                                                    key={brand.brandId}
                                                    onClick={() =>
                                                        onSelectBrand(
                                                            brand.brandId,
                                                            brand.brandName
                                                        )
                                                    }
                                                    className="cursor-pointer hover:bg-[#f8fafc] transition-colors"
                                                >
                                                    <td className="px-6 py-4">
                                                        <p className="text-base font-semibold text-[#1e293b]">
                                                            {brand.brandName}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-base text-[#1e293b]">
                                                        {formatCount(brand.totalActivePurchases)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-base text-[#1e293b]">
                                                        {formatCount(brand.totalLineItems)}
                                                    </td>
                                                    <td className="px-6 py-4 min-w-[180px]">
                                                        <PercentRow
                                                            label="By purchases"
                                                            percent={
                                                                brand.adoptionPercentByPurchases
                                                            }
                                                            denominator={brand.totalActivePurchases}
                                                            linkedCount={brand.aiLinkedPurchases}
                                                            totalCount={brand.totalActivePurchases}
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 min-w-[180px]">
                                                        <PercentRow
                                                            label="By line items"
                                                            percent={
                                                                brand.adoptionPercentByLineItems
                                                            }
                                                            denominator={brand.totalLineItems}
                                                            linkedCount={brand.aiLinkedLineItems}
                                                            totalCount={brand.totalLineItems}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-12 text-center">
                                                    <p className="text-[#64748b] font-medium">
                                                        No brands match &quot;{searchInput}&quot;
                                                    </p>
                                                    <p className="text-sm text-[#94a3b8] mt-1">
                                                        Try adjusting your search
                                                    </p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Pagination */}
                    {total > 0 && (
                        <div className="flex items-center justify-between text-sm text-[#64748b]">
                            <p>
                                Showing{' '}
                                <span className="font-medium text-[#1e293b]">
                                    {skip + 1}-{Math.min(skip + limit, total)}
                                </span>{' '}
                                of <span className="font-medium text-[#1e293b]">{total}</span>{' '}
                                brands
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() =>
                                        setCurrentPage((page) => Math.min(totalPages, page + 1))
                                    }
                                    disabled={currentPage >= totalPages}
                                    className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
